<template lang="pug">
  div(class="m-document-share-add-participant-form")
    v-tooltip(
      v-model="showWrongEdrpouTooltip"
      bottom
      nudge-top="-10"
      min-width="350"
      nudge-left="120"
      content-class="m-document-share-add-participant-form__tooltip-content"
    )
      template(v-slot:activator="{ on, attrs }")
        ValidationObserver(
          ref="emailForm"
          slim
          v-bind="attrs"
          v-on="on"
        )
          div(class="m-document-share-add-participant-form__row")
            ValidationProvider(
              :rules="emailInputRules"
              vid="email"
              v-slot="{ errors }"
              :name="$t('Recipient`s email or EDRPOU')"
              mode="lazy"
              :debounce="500"
              class="m-document-share-add-participant-form__validation-wrapper"
            )
              div(
                class="m-document-share-add-participant-form__input-group"
                :class="{ 'm-document-share-add-participant-form__input-group--error': serverError || errors.length > 0 }"
              )
                v-text-field(
                  ref="emailInput"
                  v-model.trim="email"
                  :loading="inputLoading"
                  :placeholder="$t('Enter user email or EDRPOU')"
                  hide-details
                  solo
                  flat
                  class="m-document-share-add-participant-form__email-input"
                  data-test="document-share-pro-email-input"
                  @keydown.enter.prevent="handleAddUser"
                )
                e-unified-select(
                  v-model="action"
                  :items="formattedActions"
                  custom-class="m-document-share-add-participant-form__action-select"
                  data-test="document-share-pro-action-select"
                )
              div(
                v-if="serverError || errors.length > 0"
                class="m-document-share-add-participant-form__error-message"
              ) {{ serverError || errors[0] }}
            v-btn(
              class="outlined-button md m-document-share-add-participant-form__add-btn"
              color="primary"
              outlined
              :loading="inputLoading"
              data-test="document-share-pro-add-btn"
              @click="handleAddUser"
            ) {{ $t('Add') }}
      div(class="m-document-share-add-participant-form__tooltip pa-2")
        v-btn(
          icon
          class="m-document-share-add-participant-form__tooltip-close"
          data-test="document-share-pro-tooltip-close-btn"
          @click="showWrongEdrpouTooltip = false"
        )
          e-svg-icon cross-close
        div(class="m-document-share-add-participant-form__tooltip-title") {{ $t('Send document to E-mail') }}
        div(class="m-document-share-add-participant-form__tooltip-text") {{ wrongEdrpouTooltipMessage }}
</template>

<script>
import EUnifiedSelect from '~/components/elements/selections/e-unified-select'

export default {
  name: 'MDocumentShareAddParticipantForm',
  components: {
    EUnifiedSelect
  },
  props: {
    documentId: {
      type: [String, Number],
      required: true
    },
    formattedActions: {
      type: Array,
      required: true
    },
    existingParticipants: {
      type: Array,
      default: () => []
    },
    currentUserEmail: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      email: null,
      action: 'sign',
      inputLoading: false,
      showWrongEdrpouTooltip: false,
      wrongEdrpouTooltipMessage: '',
      serverError: ''
    }
  },
  computed: {
    emailInputRules() {
      if (!this.email) return 'required'
      return /^\d+$/.test(this.email.trim())
        ? 'required|min:8|max:10'
        : 'required|email'
    },
    isEdrpou() {
      return this.email && /^\d{8,10}$/.test(this.email)
    },
    isDuplicate() {
      return this.existingParticipants.some(p =>
        p.emailOrEdrpou === this.email ||
        p.user?.email === this.email ||
        p.organization?.edrpou === this.email ||
        p.edrpou === this.email
      ) || this.email === this.currentUserEmail
    }
  },
  watch: {
    email() {
      this.serverError = ''
      this.showWrongEdrpouTooltip = false
    }
  },
  methods: {
    async handleAddUser() {
      const valid = await this.$refs.emailForm.validate()
      if (!valid) return
      if (this.isDuplicate) {
        this.$refs.emailForm.setErrors({
          email: [this.$t(this.isEdrpou ? 'This edrpou is already taken' : 'This email is already taken')]
        })
        return
      }

      try {
        this.inputLoading = true
        const payload = this.isEdrpou
          ? { edrpou: this.email, action: this.action }
          : { email: this.email, action: this.action }

        const newUser = await this.$store.dispatch('participants/createDocumentParticipant', {
          documentId: this.documentId,
          payload,
          validate: true
        })

        this.$emit('participant-added', {
          ...payload,
          id: newUser.id,
          name: newUser.organization?.name || null,
          priority: this.existingParticipants.filter(p => !p.isOwner).length
        })

        this.resetForm()

        window.dataLayer?.push({
          event: 'add_email',
          email: this.currentUserEmail
        })
      } catch (e) {
        this.handleError(e)
      } finally {
        this.inputLoading = false
      }
    },
    resetForm() {
      this.email = null
      this.serverError = ''
      this.$nextTick(() => {
        this.$refs.emailForm.reset()
      })
    },
    handleError(error) {
      const response = error?.response?.data
      const errorCode = response?.status || response?.code
      const validationErrors = response?.errors
      let errorMessage = response?.message
      if (validationErrors?.email || validationErrors?.edrpou) {
        const fieldError = validationErrors.email || validationErrors.edrpou
        errorMessage = Array.isArray(fieldError) ? fieldError[0] : fieldError
      }
      if (errorCode === 400) {
        const message = errorMessage ||
          this.$t('This organization is not yet registered in Dubidoc, we recommend sending the document to E-mail')
        this.serverError = message
        this.wrongEdrpouTooltipMessage = message
        this.showWrongEdrpouTooltip = true
        return
      }

      if (errorMessage) {
        this.serverError = errorMessage
      } else {
        this.$handlers.error(error, this)
      }
    }
  }
}
</script>

<style scoped lang="scss">
.m-document-share-add-participant-form {
  width: 100%;
  margin-top: 16px;
  &__row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    width: 100%;
  }
  &__validation-wrapper {
    flex: 1;
    position: relative;
    margin-bottom: 28px;
  }
  &__input-group {
    display: flex;
    justify-content: space-between;
    width: 100%;
    border: 1px solid $border-disabled;
    border-radius: 12px;
    overflow: hidden;
    background: $white;
    transition: border-color 0.2s ease;
    &:focus-within {
      border-color: $border-primary;
    }
    &--error {
      border-color: $danger;
      box-shadow: 0 0 0 1px rgba($danger, 0.2);
      &:focus-within {
        border-color: $danger;
      }
    }
  }
  &__email-input {
    flex: 1;
    ::v-deep {
      .v-input__control {
        min-height: $input-height;
      }
      .v-input__slot {
        box-shadow: none !important;
        border: none !important;
        background: transparent !important;
        margin: 0;
        padding: 0 12px;
      }
      .v-text-field__slot {
        padding: 12px 0;
      }
      input {
        padding: 0;
        border: none;
        outline: none;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: $dark2;
        font-size: 14px;
        font-weight: 400;
        line-height: 22px;
      }
      label {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 100%;
        color: $border-primary;
        font-size: 14px;
        font-weight: 300;
        line-height: 22px;
      }
    }
  }
  &__action-select {
    max-width: 160px;
  }
  &__add-btn {
    height: $input-height;
    min-width: 100px;
    flex-shrink: 0;
    &:disabled {
      opacity: 0.5;
    }
  }
  &__error-message {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    color: $danger;
    font-size: 12px;
    margin-top: 4px;
    padding-left: 4px;
    line-height: 1.4;
    z-index: 1;
  }
  &__tooltip {
    position: relative;

    &::before {
      content: '';
      width: 12px;
      height: 12px;
      background: $dark;
      transform: rotateZ(45deg);
      position: absolute;
      top: -6px;
      left: 0;
      z-index: map-get($zIndexes, 'base');
    }

    &-content {
      pointer-events: auto;
      background: $dark;
    }

    &-close {
      width: 25px;
      height: 25px;
      position: absolute;
      top: 0;
      right: -5px;
      z-index: map-get($zIndexes, 'overAll');
    }
    &-title {
      color: $white;
      font-size: 16px;
      font-weight: 500;
      line-height: 24px;
      margin-bottom: 8px;
    }
    &-text {
      color: rgba(255, 255, 255, 0.64);
      font-size: 12px;
      font-weight: 300;
      line-height: 18px;
    }
  }
}
</style>
